import { useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ResourceHeader as ProjectResourceHeader } from '@factorialco/f0-react/dist/experimental'
import { Clock, ExternalLink, Pencil } from '@factorialco/f0-react/icons/app'
import { ProjectManagementProjectStatusEnum } from 'generated/resources/graphql'
import moment from 'moment'

import { useNavigation } from 'modules/core/hooks/useNavigation'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { ProjectLog } from 'modules/project_management/components/ProjectLog'
import {
  getStatusColor,
  getStatusLabel,
} from 'modules/project_management/components/labelTags/StatusTag'

type Props = {
  status: ProjectManagementProjectStatusEnum
  projectName: string
  startDate: string | null | undefined
  dueDate: string | null | undefined
  code: string | null | undefined
  projectId: string
  client: string | null
  canManageProject: boolean
}

export const ProjectHeader = ({
  projectName,
  status,
  startDate,
  dueDate,
  code,
  projectId,
  client,
  canManageProject,
}: Props) => {
  const projectStartDate = startDate
    ? moment(startDate).format('DD/MM/YYYY')
    : i18n.t('project_management.headers.no_start_date')
  const projectDueDate = dueDate
    ? moment(dueDate).format('DD/MM/YYYY')
    : i18n.t('project_management.headers.no_due_date')
  const existingDate = !!dueDate || !!startDate
  const statusColor = getStatusColor(status)

  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const [openLog, setOpenLog] = useState(false)

  const getDateLabel = (): string => {
    if (startDate && dueDate) {
      return i18n.t('project_management.table.tasks.headers.dates')
    } else if (startDate) {
      return i18n.t('project_management.headers.start_date')
    } else if (dueDate) {
      return i18n.t('project_management.headers.due_date')
    } else {
      return ''
    }
  }

  const getDateValue = (): string => {
    if (startDate && dueDate) {
      return `${projectStartDate} - ${projectDueDate}`
    } else if (startDate) {
      return projectStartDate
    } else if (dueDate) {
      return projectDueDate
    } else {
      return ''
    }
  }

  return (
    <>
      <ProjectResourceHeader
        title={projectName}
        status={{
          label: i18n.t('project_management.table.projects_list.headers.status'),
          text: getStatusLabel(status),
          variant: statusColor,
          actions: [
            {
              icon: Pencil,
              label: i18n.t('common.edit'),
              onClick: () => {
                navigate(navigation.projects.id.details.settings.fullPath({ id: projectId }))
              },
            },
          ],
        }}
        metadata={[
          existingDate && {
            label: getDateLabel(),
            value: {
              type: 'text',
              content: getDateValue(),
            },
          },
          !!code && {
            label: i18n.t('project_management.table.projects_list.headers.code'),
            value: {
              type: 'text',
              content: code,
            },
          },
          !!client && {
            label: i18n.t('project_management.table.projects_list.headers.client'),
            value: {
              type: 'text',
              content: client,
            },
          },
          canManageProject && {
            label: '',
            value: {
              type: 'text',
              content: i18n.t('common.more_information'),
            },
            actions: [
              {
                label: i18n.t('common.more_information'),
                icon: ExternalLink,
                onClick: () => {
                  track(trackingEvents.projectManagement.projects.details.clickMoreInformation)
                  navigate(navigation.projects.id.details.settings.fullPath({ id: projectId }))
                },
              },
            ],
          },
        ]}
        secondaryActions={[
          {
            label: i18n.t('common.activity'),
            icon: Clock,
            onClick: () => {
              setOpenLog(true)
            },
          },
        ].filter(Boolean)}
      />
      {openLog && (
        <ProjectLog
          id={projectId}
          name={projectName}
          onClose={() => {
            setOpenLog(false)
          }}
        />
      )}
    </>
  )
}
